import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ReactComponent as CloseIcon } from "../../../../assets/images/x.svg";
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import { GROUP_STATUS_OPTIONS } from "../../../../constants/peopleData";
import { peopleSelectControlSx, peopleSelectMenuProps, peopleSelectSx } from "../peopleSelectStyles";
import {
  fieldErrorSx,
  hasValidationErrors,
  peopleFormErrorTextSx,
  validateGroupForm,
} from "../peopleFormValidation";

const fieldLabelSx = {
  color: "#405261",
  fontFamily: "Inter",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "normal",
  mb: 0.75,
};

const inputSx = {
  width: 360,
  maxWidth: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    background: "#FFF",
    boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
    fontFamily: "Inter",
    fontSize: 14,
    height: 40,
    "& fieldset": { border: "1px solid #D5DCE1" },
    "&:hover fieldset": { border: "1px solid #D5DCE1" },
    "&.Mui-focused fieldset": { border: "1px solid #D5DCE1" },
  },
  "& .MuiOutlinedInput-input": {
    py: 0,
    px: 1.5,
    height: 40,
    boxSizing: "border-box",
    "&::placeholder": { color: "#C5CED6", opacity: 1 },
  },
};

const selectSx = peopleSelectControlSx({ width: 360, maxWidth: "100%" });

const EMPTY_FORM = { name: "", status: "" };

export default function GroupFormModal({
  open,
  mode = "create",
  initialValues = null,
  onClose,
  onConfirm,
  submitting = false,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({ name: "", status: "" });
  const isEdit = mode === "edit";

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM);
      setErrors({ name: "", status: "" });
      return;
    }
    if (initialValues) {
      setForm({
        name: initialValues.name || "",
        status: initialValues.status || "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setErrors({ name: "", status: "" });
  }, [open, initialValues]);

  const set = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleSubmit = () => {
    const nextErrors = validateGroupForm(form, { requireStatus: !isEdit });
    if (hasValidationErrors(nextErrors)) {
      setErrors(nextErrors);
      return;
    }
    onConfirm({
      ...form,
      name: form.name.trim(),
      status: form.status || "active",
    });
  };

  const handleClose = () => {
    setForm(EMPTY_FORM);
    setErrors({ name: "", status: "" });
    onClose();
  };

  const statusLabel =
    GROUP_STATUS_OPTIONS.find((opt) => opt.value === form.status)?.label || "Select";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
        },
      }}
      PaperProps={{
        sx: {
          width: 408,
          height: 312,
          maxWidth: "95vw",
          borderRadius: "12px",
          border: "1px solid #D5DCE1",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          m: 2,
          boxSizing: "border-box",
        },
      }}
    >
      <Box sx={{ p: "24px", position: "relative", height: "100%", boxSizing: "border-box" }}>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: "absolute", top: 16, right: 16, p: 0.5 }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          sx={{
            color: "#061522",
            fontFamily: "Inter",
            fontSize: 18,
            fontWeight: 500,
            lineHeight: "18px",
            mb: 2.5,
          }}
        >
          {isEdit ? "Edit Group" : "Create Group"}
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography sx={fieldLabelSx}>Group Name</Typography>
            <TextField
              fullWidth
              placeholder="Enter name"
              value={form.name}
              onChange={set("name")}
              error={Boolean(errors.name)}
              sx={{ ...inputSx, ...fieldErrorSx(errors.name) }}
            />
            {errors.name ? (
              <Typography sx={peopleFormErrorTextSx}>{errors.name}</Typography>
            ) : null}
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>Status</Typography>
            <FormControl
              size="small"
              error={Boolean(errors.status)}
              sx={{ ...selectSx, ...fieldErrorSx(errors.status) }}
            >
              <Select
                displayEmpty
                value={form.status}
                onChange={set("status")}
                IconComponent={Dropdown}
                MenuProps={peopleSelectMenuProps}
                sx={peopleSelectSx}
                renderValue={() => (
                  <Typography
                    component="span"
                    sx={{
                      color: form.status ? "#061522" : "#C5CED6",
                      fontSize: 14,
                      fontFamily: "Inter",
                      fontWeight: 400,
                    }}
                  >
                    {statusLabel}
                  </Typography>
                )}
              >
                {GROUP_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily: "Inter", fontSize: 14 }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.status ? (
              <Typography sx={peopleFormErrorTextSx}>{errors.status}</Typography>
            ) : null}
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
          <Button
            onClick={handleClose}
            sx={{
              width: 176,
              height: 40,
              borderRadius: "8px",
              border: "1px solid #D5DCE1",
              background: "#FFF",
              boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              color: "#405261",
              fontFamily: "Inter",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: "18px",
              textTransform: "none",
              "&:hover": { background: "#FFF", borderColor: "#C5CED6" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              width: 176,
              height: 40,
              borderRadius: "8px",
              background: "#148DFD",
              boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              color: "#FFF",
              fontFamily: "Inter",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: "18px",
              textTransform: "none",
              "&:hover": { background: "#0077CC" },
              "&.Mui-disabled": {
                color: "#93A1B0",
                bgcolor: "#F9FAFA",
                border: "1px solid #C5CED6",
              },
            }}
          >
            {submitting ? "Saving…" : isEdit ? "Save Changes" : "Create Group"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
